<script lang="ts">
  import { faCircleInfo } from '@fortawesome/free-solid-svg-icons'
  import Icon from './Icon.svelte'
  import { inertia } from '../utils/inertia'
  import { getI18n } from '../i18n'
  import { MEDIA_CREDIT, MEDIA_LICENSE_NAME } from '../mediaLicense'

  // A badge in a corner of the media, like the compact attribution of the maps. Its parent positions it.
  let { placement = 'top' }: { placement?: 'top' | 'bottom' } = $props()

  const i18n = getI18n()
  const termsHref = $derived(i18n.path('/media-license'))

  let text = $state<HTMLElement>()
  // Until hydration the stylesheet decides: open from tablet width on, collapsed on phones.
  let expanded = $state<boolean>()

  $effect(() => {
    if (text && expanded === undefined) expanded = getComputedStyle(text).display !== 'none'
  })
</script>

<span class={['media-credit', { '-top': placement === 'top', '-bottom': placement === 'bottom', '-expanded': expanded === true, '-collapsed': expanded === false }]}>
  <button type="button" class="media-credit-button" aria-label={i18n.t('attribution.toggle')} aria-expanded={expanded} onclick={() => (expanded = !expanded)}>
    <Icon icon={faCircleInfo} />
  </button>
  <!-- Isolated, so that the Latin credit keeps its order on right-to-left pages. -->
  <span class="media-credit-text" bind:this={text}>
    <bdi>{MEDIA_CREDIT} · <a href={termsHref} {@attach inertia({ href: termsHref })} class="media-credit-link">{MEDIA_LICENSE_NAME}</a></bdi>
  </span>
</span>

<style>
  .media-credit {
    position: absolute;
    inset-inline-end: var(--space-5);
    z-index: 1;
    display: flex;
    /* Keeps the button first in the focus order, but at the end of the badge. */
    flex-direction: row-reverse;
    align-items: center;
    max-width: calc(100% - 2 * var(--space-5));
    min-height: var(--space-9);
    border-radius: var(--radius-4);
    background-color: var(--color-white);
    color: var(--color-text);
    font-size: var(--font-size-3);
    line-height: var(--line-height-3);
    box-shadow: var(--box-shadow-1);
  }

  .media-credit.-top {
    inset-block-start: var(--space-5);
  }

  .media-credit.-bottom {
    inset-block-end: var(--space-5);
  }

  .media-credit-button {
    flex: none;
    display: grid;
    place-items: center;
    width: var(--space-9);
    height: var(--space-9);
    padding: 0;
    border: none;
    border-radius: var(--radius-6);
    background: none;
    color: inherit;
    font-size: var(--font-size-5);
    cursor: pointer;

    &:focus-visible {
      outline: var(--border-2) solid var(--color-variant-brand);
      outline-offset: var(--border-1);
    }
  }

  .media-credit.-expanded .media-credit-button {
    background-color: var(--shadow-0);
  }

  .media-credit-text {
    display: none;
    min-width: 0;
    padding-block: var(--space-1);
    padding-inline: var(--space-4) var(--space-2);

    @media (--tablet) {
      display: block;
    }
  }

  .media-credit.-expanded .media-credit-text {
    display: block;
  }

  .media-credit.-collapsed .media-credit-text {
    display: none;
  }

  .media-credit-link {
    color: inherit;

    &:hover {
      color: var(--color-variant-brand);
    }
  }
</style>
